import * as React from 'react';
import { BCConfigProvider } from '..';
import { AntdLocale } from '../BCIntl';
import { ThemeChanger } from './components/ThemeChanger';
import { IThemeChangerProps } from './entities/IThemeChangerProps';
import { MLHelper, StyleContainerProvider } from '@bimser/core';
import LoadingSVG from '../common/assets/loadingIndicator.svg';

const defaultVariables = {
	backgroundColor: '#b2d8ff',
	image: 'Theme1',
	primaryColor: '#0078d4',
	goldenColor: '#f5a623',
	fontFamily: 'Segoe WP',
	secondaryIconColor: '#252421'
};

const getSessionStorage = (): Partial<typeof defaultVariables> => {
	const theme = sessionStorage.getItem('theme');
	if (theme) {
		try {
			const parsedTheme = JSON.parse(theme);

			return {
				...defaultVariables,
				backgroundColor: parsedTheme.backgroundColor || defaultVariables.backgroundColor,
				image: parsedTheme.image || defaultVariables.image,
				primaryColor: parsedTheme.primaryColor || defaultVariables.primaryColor
			};
		} catch (error) {
			console.warn('Local storage theme cannot handled correctly');
			return null;
		}
	}

	return null;
};

const BCThemeChanger = ({
	children,
	environment,
	styleProviderContainer,
	getPopupContainer
}: IThemeChangerProps) => {
	const [variables, setVariables] = React.useState(getSessionStorage() || defaultVariables);
	const defaultCulture = MLHelper.defaultLanguage?.culture || 'en-US';
	const loginStatus = localStorage.getItem('token') || sessionStorage.getItem('token');

	React.useLayoutEffect(() => {
		const setVariablesEvent = () => {
			if (environment === 'IDE' || !loginStatus) {
				setVariables({
					...defaultVariables,
					backgroundColor: '#fff',
					image: 'Theme4'
				});
				return;
			}

			setVariables(getSessionStorage() || defaultVariables);
		};

		setVariablesEvent();

		window.addEventListener('themeLoaded', setVariablesEvent);

		return () => {
			window.removeEventListener('themeLoaded', setVariablesEvent);
		};
	}, [loginStatus]);

	const getLanguage = (defaultLocale: string, locale?: string): any => {
		const lc = locale || defaultLocale;
		switch (lc) {
			case 'ru':
				return AntdLocale.ru_RU;
			case 'en':
				return AntdLocale.en_US;
			case 'zh':
				return AntdLocale.zh_TW;
			case 'az':
				return AntdLocale.az_AZ;
			case 'de':
				return AntdLocale.de_DE;
			default: {
				return AntdLocale.tr_TR;
			}
		}
	};

	const antdLanguage = React.useMemo(() => {
		const defaultLocale = defaultCulture.slice(0, 2);
		const locale = localStorage.locale ? localStorage.locale.slice(0, 2) : defaultLocale;
		return getLanguage(defaultLocale, locale);
	}, [defaultCulture, localStorage.locale]);

	return (
		<BCConfigProvider
			theme={{
				token: {
					colorPrimary: variables.primaryColor,
					colorInfo: variables.primaryColor,
					borderRadius: 1.5,
					fontFamily:
						"-apple-system, BlinkMacSystemFont, 'Segoe WP', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
				},
				components: {
					Spin: {
						dotSize: 32,
						dotSizeSM: 24,
						dotSizeLG: 48
					}
				},
				hashed: true
			}}
			spin={{ indicator: <img src={LoadingSVG} /> }}
			locale={antdLanguage}
			getPopupContainer={getPopupContainer}
		>
			<ThemeChanger
				primaryColor={variables.primaryColor}
				goldenColor={variables.goldenColor}
				fontFamily={variables.fontFamily}
				secondaryIconColor={variables.secondaryIconColor}
				backgroundColor={variables.backgroundColor}
				backgroundImage={variables.image}
			>
				<StyleContainerProvider container={styleProviderContainer}>
					{children}
				</StyleContainerProvider>
			</ThemeChanger>
		</BCConfigProvider>
	);
};

export { IThemeChangerProps };
export default BCThemeChanger;
